/**
 * ApprovedTemplatesPanel — displays exactly the four approved templates as a
 * single spined, columnar list (row = spine / name+description / word-count
 * +length-bar) — Direction B, folded in from the standalone rows prototype
 * (ApprovedTemplatesPanelRows). This is the canonical component; the card
 * grid / TemplateCard import is gone.
 *
 * Which four, in what order, what happens when one cannot be offered, and
 * which one is pre-selected all live in constants/approvedBlogTemplates —
 * this file only renders the result.
 *
 * Resolution is UUID-only. Missing or gate-conflicted approved records are
 * omitted; no slug/name match and no fifth fallback identity can enter this
 * client surface.
 *
 * Pre-selection is quiet (R-QC-8): the default is expressed as selection
 * state, never as endorsement chrome. A host may inject a suggested id via
 * `suggestedTemplateId` (R-QC-7 seam) — e.g. from the keyword-aware
 * suggestion — and it is honoured only if it is one of the approved four; an
 * off-allowlist suggestion is silently ignored, so the picker can never
 * surface an unapproved template. The host's own `selectedId` also only wins
 * when it names an offered template — a `selectedId` that isn't one of the
 * templates actually on screen (stale prop, race, bad caller) falls back to
 * the same quiet default rather than rendering nothing selected.
 *
 * No Customize/Delete/overflow on this surface (A1) — this list offers
 * selection and Use only, nothing else. A client surface that needs those
 * affordances uses TemplateCard directly, not this panel.
 *
 * Word counts (A2) come from the CATALOG RECORD (`default_config.
 * targetWordCount`), not a constants lookup — the constants file states
 * identity/order/fallback, not display copy that can drift from the record a
 * client actually has tagged. A template whose record has no word count
 * renders neither the "≈N words" figure nor the length bar — never an empty
 * track pretending to be data.
 *
 * Selection reads via exactly THREE encodings (R3 in the design spec): filled
 * radio glyph, colored spine, coral-light row wash. Name ink and the
 * length-bar color are deliberately left unchanged by selection so state
 * reads as state, not as decoration. Resting spine is the faint teal-light
 * token, not mid-grey teal — a mid-grey resting spine reads at nearly the
 * same luminance as the selected coral spine in grayscale, which would kill
 * one of the three required encodings; selected stays coral.
 *
 * Interaction is a single-select radiogroup (WAI-ARIA radio pattern): one row
 * is ever a Tab stop (roving tabindex, the selected row); Up/Left and
 * Down/Right move focus AND selection to the adjacent row; Home/End jump to
 * the first/last row; Enter/Space commits the focused row. The shared footer
 * `Use` button is its own tab stop outside the radiogroup and always names
 * the commitment ("Use Default SEO Blog"), never a bare "Use".
 */

import React, { useMemo, useRef } from 'react';
import { BlogTemplateSummary } from '@/services/blogTemplatesService';
import { useBlogTemplates } from '@/hooks/useBlogTemplates';
import {
  APPROVED_TEMPLATE_COUNT,
  ResolvedApprovedTemplate,
  resolveApprovedTemplates,
} from '@/constants/approvedBlogTemplates';
import { cn } from '@/lib/utils';

export interface ApprovedTemplatesPanelProps {
  /**
   * Currently selected template id, once the host tracks an explicit choice.
   * Only honoured when it names one of the offered templates — an id that
   * isn't in the offered set (stale prop, race, bad caller) falls back to the
   * quiet default rather than leaving nothing selected.
   */
  selectedId?: string;
  /**
   * Injected suggestion (R-QC-7 seam). Honoured as the quiet default only when
   * it is one of the approved four; ignored otherwise. Never overrides a
   * client choice — `selectedId` takes precedence when it's valid.
   */
  suggestedTemplateId?: string;
  /**
   * Fires on click, and on every arrow/Home/End/Enter/Space move within the
   * radiogroup. This is a CONTROLLED component — it does not track its own
   * selection — so the host must echo the new id back through `selectedId`
   * synchronously, or keyboard-navigated selection visuals lag one step
   * behind DOM focus.
   */
  onSelect: (template: BlogTemplateSummary) => void;
  /**
   * Optional: the footer "Use {name}" button renders only when supplied. A host
   * that owns its own commit action (e.g. a wizard whose Create step is the
   * real primary) omits it, so the picker is selection-only and the surface
   * carries a single primary rather than two competing ones.
   */
  onUse?: (template: BlogTemplateSummary) => void;
}

const wordsFor = (template: ResolvedApprovedTemplate): number | null =>
  template.default_config?.targetWordCount ?? null;

export const ApprovedTemplatesPanel: React.FC<ApprovedTemplatesPanelProps> = ({
  selectedId,
  suggestedTemplateId,
  onSelect,
  onUse,
}) => {
  const { data, isLoading, isError } = useBlogTemplates();
  const rowRefs = useRef<Array<HTMLLIElement | null>>([]);

  const { templates, preselectedTemplateId } = useMemo(
    () =>
      resolveApprovedTemplates(data?.templates ?? [], {
        preselectedTemplateId: suggestedTemplateId,
      }),
    [data, suggestedTemplateId],
  );

  // The client's explicit choice wins, but only if it names a template that's
  // actually offered (A4b) — otherwise the quiet default stands rather than
  // rendering a selectedId that doesn't correspond to any visible row.
  const selectedIsOffered = selectedId !== undefined && templates.some((t) => t.id === selectedId);
  const activeId = (selectedIsOffered ? selectedId : preselectedTemplateId) ?? undefined;
  // templates.length > 0 is guaranteed past the empty-state return below, and
  // preselectedTemplateId always names templates[0] at minimum, so this only
  // falls back to templates[0] defensively — it never actually needs to.
  const selectedTemplate = templates.find((t) => t.id === activeId) ?? templates[0];

  // A2: length-bar width is relative to the words actually being displayed —
  // templates with no word count don't drag the scale down or get counted.
  const maxWords = useMemo(() => {
    const displayed = templates
      .map((t) => wordsFor(t))
      .filter((words): words is number => words !== null);
    return Math.max(1, ...displayed);
  }, [templates]);

  if (isLoading) {
    return (
      <div>
        <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-teal">
          Templates
        </p>
        <ul
          role="status"
          aria-label="Loading templates"
          className="list-none border-t border-teal-light p-0 m-0"
        >
          {Array.from({ length: APPROVED_TEMPLATE_COUNT }).map((_, index) => (
            <li
              key={index}
              className="grid grid-cols-[3px_1fr_auto] items-center gap-3 border-b border-teal-light py-3.5 pl-3 pr-1"
            >
              <span className="self-stretch w-[3px] rounded-sm bg-teal-light" aria-hidden="true" />
              <span className="min-w-0">
                <span className="mb-2 block h-[10px] w-[70%] animate-pulse rounded bg-teal-light motion-reduce:animate-none" />
                <span className="block h-2 w-[90%] animate-pulse rounded bg-teal-light motion-reduce:animate-none" />
              </span>
              <span className="block h-3 w-9 animate-pulse rounded bg-teal-light motion-reduce:animate-none" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Templates could not be loaded.
      </p>
    );
  }

  if (templates.length === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Templates aren&apos;t available right now.
      </p>
    );
  }

  const commit = (index: number, focus: boolean) => {
    const next = templates[index];
    if (!next) return;
    onSelect(next);
    if (focus) rowRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        commit((index + 1) % templates.length, true);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        commit((index - 1 + templates.length) % templates.length, true);
        break;
      case 'Home':
        event.preventDefault();
        commit(0, true);
        break;
      case 'End':
        event.preventDefault();
        commit(templates.length - 1, true);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(index, false);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <p className="mb-2.5 text-[11px] font-bold uppercase tracking-wide text-teal">Templates</p>
      <ul role="radiogroup" aria-label="Templates" className="list-none border-t border-teal-light p-0 m-0">
        {templates.map((template, index) => {
          const isSelected = template.id === activeId;
          const words = wordsFor(template);
          const pct = words !== null ? Math.round((words / maxWords) * 100) : 0;

          return (
            <li
              key={template.id}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={isSelected ? 0 : -1}
              data-selected={isSelected || undefined}
              onClick={() => onSelect(template)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'grid grid-cols-[3px_1fr_auto] items-center gap-3 border-b border-teal-light py-3.5 pl-3 pr-1 cursor-pointer outline-none',
                isSelected ? 'bg-coral-light' : 'hover:bg-teal-light',
                isSelected
                  ? 'focus-visible:shadow-[inset_0_0_0_2px_var(--color-ring),inset_0_0_0_3px_var(--color-coral-dark)] rounded-md'
                  : 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset rounded-md',
              )}
            >
              {/* Resting spine is the faint teal-light token, not mid-grey teal —
                  a mid-grey resting spine reads at nearly the same luminance as
                  the selected coral spine, which kills selection legibility in
                  grayscale (one of Direction B's three required encodings).
                  Selected stays coral; only resting changed. */}
              <span
                className={cn('self-stretch w-[3px] rounded-sm', isSelected ? 'bg-coral' : 'bg-teal-light')}
                aria-hidden="true"
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span
                    className={cn(
                      'inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border-[1.5px]',
                      isSelected ? 'border-coral bg-coral' : 'border-teal',
                    )}
                    aria-hidden="true"
                  >
                    {isSelected && (
                      <svg viewBox="0 0 16 16" fill="none" className="h-2.5 w-2.5">
                        <path
                          d="M3.5 8.2l2.8 2.8 6.2-6.4"
                          stroke="#fff"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="text-[15px] font-semibold tracking-tight text-foreground">
                    {template.name}
                  </span>
                </span>
                {template.description && (
                  <span className="mt-[3px] block truncate text-[12.5px] leading-snug text-teal-dark">
                    {template.description}
                  </span>
                )}
              </span>
              <span className="flex flex-col items-end gap-1.5 text-right">
                {words !== null && (
                  <span className="whitespace-nowrap text-[13px] font-bold tabular-nums text-teal-dark">
                    ≈{words.toLocaleString()}
                    <span className="ml-0.5 text-[11px] font-medium text-teal-dark">words</span>
                  </span>
                )}
                {words !== null && (
                  <span className="h-1 w-16 overflow-hidden rounded-sm bg-teal-light">
                    <span className="block h-full rounded-sm bg-teal" style={{ width: `${pct}%` }} />
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {onUse && (
        <div className="px-1 pt-3.5">
          <button
            type="button"
            className="flex min-h-11 w-full items-center justify-center rounded-lg bg-coral px-3 text-sm font-bold tracking-wide text-white hover:bg-coral-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onUse(selectedTemplate)}
          >
            Use {selectedTemplate.name}
          </button>
        </div>
      )}
    </div>
  );
};
